import React, { useState, useEffect, useCallback } from "react";
import {
  Layout,
  Card,
  Table,
  Button,
  Space,
  Tag,
  Typography,
  Row,
  Col,
  Statistic,
  Input,
  Modal,
  Form,
  Avatar,
  Tooltip,
  Badge,
  Empty,
} from "antd";
import {
  FaSearch,
  FaEye,
  FaCheckCircle,
  FaTimesCircle,
  FaExclamationTriangle,
  FaCalendarAlt,
  FaUsers,
  FaFilter,
  FaFileAlt,
  FaClock,
  FaUserCheck,
  FaFileMedical,
  FaExternalLinkAlt,
  FaCommentAlt,
} from "react-icons/fa";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Api from "../api/Api";
import dayjs from "dayjs";

const { Content } = Layout;
const { Title, Text } = Typography;
const { TextArea } = Input;

/* ─────────────────────────────────────────────────────────────────────────────
   HELPERS
───────────────────────────────────────────────────────────────────────────── */
const formatDateTime = (str) => {
  if (!str) return "—";
  try {
    return dayjs(str).format("DD/MM/YYYY HH:mm");
  } catch {
    return str;
  }
};

const formatDate = (str) => {
  if (!str) return "—";
  try {
    return dayjs(str).format("DD/MM/YYYY");
  } catch {
    return str;
  }
};

const getJustificadoInfo = (justificado) => {
  if (justificado === "S") return { label: "APROVADA", color: "success", icon: <FaCheckCircle /> };
  if (justificado === "N" && false) return {}; // placeholder
  return { label: "PENDENTE", color: "warning", icon: <FaClock /> };
};

/* ─────────────────────────────────────────────────────────────────────────────
   MODAL DE VER DETALHES
───────────────────────────────────────────────────────────────────────────── */
const DetalhesModal = ({ open, onClose, falta }) => {
  if (!falta) return null;

  const rows = [
    { label: "Colaborador", value: falta.colaborador },
    { label: "Motivo", value: falta.motivo },
    { label: "Início", value: formatDateTime(falta.inicio) },
    { label: "Fim", value: formatDateTime(falta.fim) },
    { label: "Observação do colaborador", value: falta.obs || "—" },
    { label: "Observação do diretor", value: falta.obs2 || "—" },
    { label: "Justificador", value: falta.justificador || "—" },
    { label: "Data de aprovação", value: falta.data_aprovacao ? formatDate(falta.data_aprovacao) : "—" },
  ];

  return (
    <Modal
      open={open}
      onCancel={onClose}
      width={580}
      footer={[
        <Button key="close" onClick={onClose} style={{ borderRadius: 8 }}>
          Fechar
        </Button>,
        falta.justificativo && (
          <Button
            key="doc"
            type="primary"
            icon={<FaExternalLinkAlt />}
            onClick={() => window.open(falta.justificativo, "_blank")}
            style={{ borderRadius: 8 }}
          >
            Ver Documento
          </Button>
        ),
      ].filter(Boolean)}
      title={
        <Space>
          <FaFileAlt style={{ color: "#1677ff", fontSize: 18 }} />
          <span style={{ fontWeight: 600, fontSize: 17 }}>Detalhes da Falta</span>
        </Space>
      }
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 0, marginTop: 8 }}>
        {rows.map(({ label, value }) => (
          <div
            key={label}
            style={{
              display: "flex",
              justifyContent: "space-between",
              padding: "10px 0",
              borderBottom: "1px solid #f0f0f0",
            }}
          >
            <Text type="secondary" style={{ fontSize: 13, minWidth: 180 }}>{label}</Text>
            <Text style={{ fontSize: 13, textAlign: "right", maxWidth: 280 }}>{value}</Text>
          </div>
        ))}
      </div>
    </Modal>
  );
};

/* ─────────────────────────────────────────────────────────────────────────────
   MODAL DE APROVAÇÃO/REJEIÇÃO
───────────────────────────────────────────────────────────────────────────── */
const AprovarModal = ({ open, onClose, onSuccess, falta, acao, userStored }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) form.resetFields();
  }, [open, form]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      const payload = {
        id: falta.id,
        justificado: acao === "aprovar" ? "S" : "N",
        justificador: userStored.nome,
        obs2: values.obs2 || null,
      };

      await Api.JustificarFalta(payload);
      toast.success(
        acao === "aprovar" ? "Falta aprovada com sucesso!" : "Falta rejeitada."
      );
      form.resetFields();
      onSuccess();
      onClose();
    } catch (error) {
      if (error.errorFields) {
        toast.error("Preencha todos os campos obrigatórios.");
      } else {
        toast.error("Erro ao processar solicitação.");
      }
    } finally {
      setLoading(false);
    }
  };

  const isAprovar = acao === "aprovar";

  return (
    <Modal
      open={open}
      onCancel={onClose}
      width={520}
      title={
        <Space>
          {isAprovar
            ? <FaCheckCircle style={{ color: "#52c41a", fontSize: 18 }} />
            : <FaTimesCircle style={{ color: "#ff4d4f", fontSize: 18 }} />}
          <span style={{ fontWeight: 600, fontSize: 17 }}>
            {isAprovar ? "Aprovar Falta" : "Rejeitar Falta"}
          </span>
        </Space>
      }
      footer={[
        <Button key="cancel" onClick={onClose} style={{ borderRadius: 8 }}>
          Cancelar
        </Button>,
        <Button
          key="submit"
          type="primary"
          danger={!isAprovar}
          onClick={handleSubmit}
          loading={loading}
          style={{
            borderRadius: 8,
            fontWeight: 500,
            backgroundColor: isAprovar ? "#52c41a" : undefined,
            borderColor: isAprovar ? "#52c41a" : undefined,
          }}
        >
          {isAprovar ? "Confirmar Aprovação" : "Confirmar Rejeição"}
        </Button>,
      ]}
    >
      {falta && (
        <div
          style={{
            background: isAprovar ? "#f6ffed" : "#fff1f0",
            border: `1px solid ${isAprovar ? "#b7eb8f" : "#ffa39e"}`,
            borderRadius: 10,
            padding: "14px 18px",
            marginBottom: 20,
          }}
        >
          <Text strong style={{ fontSize: 14 }}>{falta.colaborador}</Text>
          <br />
          <Text type="secondary" style={{ fontSize: 13 }}>
            {falta.motivo} · {formatDateTime(falta.inicio)} → {formatDateTime(falta.fim)}
          </Text>
          {falta.obs && (
            <div style={{ marginTop: 8 }}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                <FaCommentAlt style={{ marginRight: 4 }} />
                {falta.obs}
              </Text>
            </div>
          )}
        </div>
      )}

      <Form form={form} layout="vertical">
        <Form.Item
          label={<span style={{ fontWeight: 500 }}>Observação (opcional)</span>}
          name="obs2"
        >
          <TextArea
            rows={4}
            placeholder={
              isAprovar
                ? "Alguma observação sobre a aprovação..."
                : "Motivo da rejeição..."
            }
            maxLength={250}
            showCount
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

/* ─────────────────────────────────────────────────────────────────────────────
   MAIN PAGE
───────────────────────────────────────────────────────────────────────────── */
const AprovacaoFaltas = () => {
  const [faltas, setFaltas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("TODAS");

  const [detalhesModal, setDetalhesModal] = useState({ open: false, falta: null });
  const [aprovarModal, setAprovarModal] = useState({ open: false, falta: null, acao: null });

  const userStored = JSON.parse(localStorage.getItem("user")) || {};

  useEffect(() => {
    document.title = "Aprovação de Faltas | Dashboard";
  }, []);

  const loadFaltas = useCallback(async () => {
    try {
      setLoading(true);
const payload = {diretor: userStored.diretor,direcao: userStored.direcao}
      const response = await Api.ListFaltaAprovacao(payload);
      setFaltas(Array.isArray(response) ? response : []);
    } catch {
      toast.error("Erro ao carregar faltas.");
      setFaltas([]);
    } finally {
      setLoading(false);
    }
  }, [userStored.diretor]);

  useEffect(() => {
    loadFaltas();
  }, [loadFaltas]);

  const countPendentes = faltas.filter((f) => f.justificado === "N").length;
  const countAprovadas = faltas.filter((f) => f.justificado === "S").length;
  const countComDocumento = faltas.filter((f) => f.justificativo).length;

  const filtered = faltas.filter((f) => {
    const matchFilter =
      filter === "TODAS" ||
      (filter === "PENDENTE" && f.justificado === "N") ||
      (filter === "APROVADA" && f.justificado === "S");

    const q = search.toLowerCase();
    const matchSearch =
      !q ||
      f.colaborador?.toLowerCase().includes(q) ||
      f.motivo?.toLowerCase().includes(q) ||
      f.obs?.toLowerCase().includes(q);

    return matchFilter && matchSearch;
  });

  const columns = [
    {
      title: "Colaborador",
      key: "colaborador",
      width: 220,
      render: (_, record) => (
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <Avatar
            size={40}
            style={{
              backgroundColor: "#e6f4ff",
              color: "#1677ff",
              fontWeight: 700,
              fontSize: 15,
              flexShrink: 0,
            }}
          >
            {record.colaborador?.charAt(0) || "?"}
          </Avatar>
          <div>
            <div style={{ fontWeight: 600, color: "#1f1f1f", marginBottom: 2 }}>
              {record.colaborador}
            </div>
            <Text type="secondary" style={{ fontSize: 11 }}>
              {record.employee_id}
            </Text>
          </div>
        </div>
      ),
    },
    {
      title: "Motivo",
      dataIndex: "motivo",
      key: "motivo",
      width: 140,
      render: (motivo) => (
        <Tag
          icon={<FaFileMedical style={{ marginRight: 4 }} />}
          style={{
            borderRadius: 20,
            padding: "3px 10px",
            fontWeight: 500,
            background: "#f0f5ff",
            color: "#2f54eb",
            border: "1px solid #adc6ff",
            display: "inline-flex",
            alignItems: "center",
          }}
        >
          {motivo}
        </Tag>
      ),
    },
    {
      title: "Período",
      key: "periodo",
      width: 200,
      render: (_, record) => (
        <Space direction="vertical" size={2}>
          <Text style={{ fontSize: 12 }}>
            <FaCalendarAlt style={{ marginRight: 5, color: "#8c8c8c" }} />
            <strong>Início:</strong> {formatDateTime(record.inicio)}
          </Text>
          <Text style={{ fontSize: 12 }}>
            <FaCalendarAlt style={{ marginRight: 5, color: "#8c8c8c" }} />
            <strong>Fim:</strong> {formatDateTime(record.fim)}
          </Text>
        </Space>
      ),
    },
    {
      title: "Observação",
      dataIndex: "obs",
      key: "obs",
      width: 200,
      render: (obs) =>
        obs ? (
          <Tooltip title={obs}>
            <Text
              ellipsis
              style={{ maxWidth: 180, fontSize: 13, display: "block" }}
            >
              {obs}
            </Text>
          </Tooltip>
        ) : (
          <Text type="secondary">—</Text>
        ),
    },
    {
      title: "Documento",
      key: "justificativo",
      width: 110,
      render: (_, record) =>
        record.justificativo ? (
          <Button
            type="link"
            icon={<FaExternalLinkAlt />}
            onClick={() => window.open(record.justificativo, "_blank")}
            style={{ padding: 0, fontSize: 13 }}
          >
            Ver PDF
          </Button>
        ) : (
          <Text type="secondary" style={{ fontSize: 13 }}>
            Sem doc.
          </Text>
        ),
    },
    {
      title: "Status",
      dataIndex: "justificado",
      key: "status",
      width: 130,
      render: (justificado, record) => {
        if (justificado === "S") {
          return (
            <Space direction="vertical" size={0}>
              <Tag
                color="success"
                style={{
                  borderRadius: 20,
                  padding: "3px 12px",
                  fontWeight: 500,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                }}
              >
                <FaCheckCircle /> APROVADA
              </Tag>
              {record.justificador && (
                <Text type="secondary" style={{ fontSize: 11, paddingLeft: 4 }}>
                  por {record.justificador}
                </Text>
              )}
            </Space>
          );
        }
        return (
          <Tag
            color="warning"
            style={{
              borderRadius: 20,
              padding: "3px 12px",
              fontWeight: 500,
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            <FaClock /> PENDENTE
          </Tag>
        );
      },
    },
    {
      title: "Ações",
      key: "actions",
      width: 140,
      fixed: "right",
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="Ver detalhes">
            <Button
              type="text"
              icon={<FaEye />}
              onClick={() => setDetalhesModal({ open: true, falta: record })}
              style={{ color: "#1677ff" }}
            />
          </Tooltip>
          {record.justificado === "N" && (
            <>
              <Tooltip title="Aprovar falta">
                <Button
                  type="text"
                  icon={<FaCheckCircle />}
                  onClick={() =>
                    setAprovarModal({ open: true, falta: record, acao: "aprovar" })
                  }
                  style={{ color: "#52c41a" }}
                />
              </Tooltip>
              <Tooltip title="Rejeitar falta">
                <Button
                  type="text"
                  icon={<FaTimesCircle />}
                  onClick={() =>
                    setAprovarModal({ open: true, falta: record, acao: "rejeitar" })
                  }
                  style={{ color: "#ff4d4f" }}
                />
              </Tooltip>
            </>
          )}
        </Space>
      ),
    },
  ];

  return (
    <Layout style={{ background: "transparent" }}>
      <Content style={{ padding: "24px 32px" }}>
        <ToastContainer position="top-right" autoClose={4000} />

        {/* Cabeçalho */}
        <div
          style={{
              background: "linear-gradient(135deg, #1677ff 0%, #4096ff 100%)",
            borderRadius: 16,
            padding: "24px 32px",
            marginBottom: 32,
            boxShadow: "0 8px 20px rgba(19, 194, 194, 0.18)",
          }}
        >
          <Row align="middle" justify="space-between">
            <Col>
              <Space size="middle">
                <div
                  style={{
                    backgroundColor: "rgba(255,255,255,0.2)",
                    borderRadius: 12,
                    padding: 10,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <FaUserCheck style={{ fontSize: 32, color: "#fff" }} />
                </div>
                <div>
                  <Title level={2} style={{ color: "#fff", margin: 0, fontWeight: 700 }}>
                    Aprovação de Faltas
                  </Title>
                  <Text style={{ color: "rgba(255,255,255,0.85)", fontSize: 15 }}>
                    Analise e justifique as ausências da sua equipa
                  </Text>
                </div>
              </Space>
            </Col>
          </Row>
        </div>

        {/* Cards de estatísticas */}
        <Row gutter={[24, 24]} style={{ marginBottom: 32 }}>
          <Col xs={24} sm={12} lg={8}>
            <Card
              bordered={false}
              style={{
                borderRadius: 16,
                boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
                border: "1px solid #f0f0f0",
              }}
              bodyStyle={{ padding: "20px 24px" }}
            >
              <Statistic
                title={<span style={{ fontSize: 14, color: "#8c8c8c" }}>Total de Faltas</span>}
                value={faltas.length}
                valueStyle={{ fontSize: 32, fontWeight: 700, color: "#13c2c2" }}
                prefix={
                  <div
                    style={{
                      backgroundColor: "#e6fffb",
                      borderRadius: 12,
                      padding: 10,
                      marginRight: 16,
                    }}
                  >
                    <FaUsers style={{ fontSize: 22, color: "#13c2c2" }} />
                  </div>
                }
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={8}>
            <Card
              bordered={false}
              style={{
                borderRadius: 16,
                boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
                border: "1px solid #f0f0f0",
              }}
              bodyStyle={{ padding: "20px 24px" }}
            >
              <Statistic
                title={<span style={{ fontSize: 14, color: "#8c8c8c" }}>Pendentes</span>}
                value={countPendentes}
                valueStyle={{ fontSize: 32, fontWeight: 700, color: "#faad14" }}
                prefix={
                  <div
                    style={{
                      backgroundColor: "#fffbe6",
                      borderRadius: 12,
                      padding: 10,
                      marginRight: 16,
                    }}
                  >
                    <FaClock style={{ fontSize: 22, color: "#faad14" }} />
                  </div>
                }
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={8}>
            <Card
              bordered={false}
              style={{
                borderRadius: 16,
                boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
                border: "1px solid #f0f0f0",
              }}
              bodyStyle={{ padding: "20px 24px" }}
            >
              <Statistic
                title={<span style={{ fontSize: 14, color: "#8c8c8c" }}>Aprovadas</span>}
                value={countAprovadas}
                valueStyle={{ fontSize: 32, fontWeight: 700, color: "#52c41a" }}
                prefix={
                  <div
                    style={{
                      backgroundColor: "#f6ffed",
                      borderRadius: 12,
                      padding: 10,
                      marginRight: 16,
                    }}
                  >
                    <FaCheckCircle style={{ fontSize: 22, color: "#52c41a" }} />
                  </div>
                }
              />
            </Card>
          </Col>
        </Row>

        {/* Barra de ferramentas */}
        <Card
          bordered={false}
          style={{
            borderRadius: 16,
            marginBottom: 24,
            boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
          }}
          bodyStyle={{ padding: "16px 24px" }}
        >
          <Row gutter={16} align="middle">
            <Col flex="auto">
              <Input
                size="large"
                prefix={<FaSearch style={{ color: "#bfbfbf" }} />}
                placeholder="Pesquisar por colaborador, motivo ou observação..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                allowClear
                style={{ borderRadius: 30 }}
              />
            </Col>
            <Col>
              <Space>
                {["TODAS", "PENDENTE", "APROVADA"].map((f) => (
                  <Button
                    key={f}
                    type={filter === f ? "primary" : "default"}
                    onClick={() => setFilter(f)}
                    style={{
                      borderRadius: 30,
                      fontWeight: 500,
                      ...(filter === f && f === "PENDENTE"
                        ? { backgroundColor: "#faad14", borderColor: "#faad14" }
                        : {}),
                      ...(filter === f && f === "APROVADA"
                        ? { backgroundColor: "#52c41a", borderColor: "#52c41a" }
                        : {}),
                    }}
                  >
                    {f === "TODAS" ? "Todas" : f === "PENDENTE" ? "Pendentes" : "Aprovadas"}
                  </Button>
                ))}
              </Space>
            </Col>
          </Row>
        </Card>

        {/* Tabela */}
        <Card
          bordered={false}
          style={{
            borderRadius: 16,
            boxShadow: "0 4px 16px rgba(0,0,0,0.05)",
            overflow: "hidden",
          }}
          bodyStyle={{ padding: 0 }}
        >
          <Table
            columns={columns}
            dataSource={filtered}
            rowKey="id"
            loading={loading}
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showTotal: (total) => `Total de ${total} faltas`,
              style: { padding: "16px 24px" },
            }}
            locale={{ emptyText: <Empty description="Nenhuma falta encontrada" /> }}
            scroll={{ x: 1100 }}
            rowClassName={(record) =>
              record.justificado === "N" ? "row-pendente" : ""
            }
          />
        </Card>

        {/* Modais */}
        <DetalhesModal
          open={detalhesModal.open}
          onClose={() => setDetalhesModal({ open: false, falta: null })}
          falta={detalhesModal.falta}
        />

        <AprovarModal
          open={aprovarModal.open}
          onClose={() => setAprovarModal({ open: false, falta: null, acao: null })}
          onSuccess={loadFaltas}
          falta={aprovarModal.falta}
          acao={aprovarModal.acao}
          userStored={userStored}
        />
      </Content>
    </Layout>
  );
};

export default AprovacaoFaltas;